import { AlertTriangle, CreditCard, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";

export const PaymentWarningBanner = () => {
  const navigate = useNavigate();
  const { inGracePeriod, gracePeriodEnd, failureCount, lastPaymentError, status } = useSubscription();
  const [loadingPortal, setLoadingPortal] = useState(false);

  // Show for pending-payment statuses (inclui boleto aguardando compensação)
  if (!["past_due", "unpaid", "incomplete"].includes(status ?? "") && !inGracePeriod) return null;

  const daysRemaining = gracePeriodEnd 
    ? Math.ceil((new Date(gracePeriodEnd).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        const win = window.open(data.url, "_blank");
        if (!win) window.location.href = data.url;
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      toast.error("Não foi possível abrir o portal de pagamento. Tente novamente.");
    } finally {
      setLoadingPortal(false);
    }
  };

  return (
    <Alert className="border-destructive/50 bg-destructive/10 mb-4">
      <AlertTriangle className="h-4 w-4 text-destructive" />
      <AlertDescription className="ml-2 flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-semibold text-destructive mb-1">
            Pagamento pendente — seu agente está pausado
          </p>
          <p className="text-sm text-muted-foreground">
            {failureCount > 0 && (
              <span className="block mb-1">
                Tentativas de cobrança falharam: {failureCount}x
              </span>
            )}
            {lastPaymentError && (
              <span className="block mb-1 text-xs">
                Motivo: {lastPaymentError}
              </span>
            )}
            <span className="font-medium text-foreground">
              {daysRemaining != null && daysRemaining > 0
                ? `Regularize em até ${daysRemaining} dias para evitar o cancelamento.`
                : "Atualize seu pagamento para reativar os serviços."}
            </span>
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleManageSubscription}
            disabled={loadingPortal}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            {loadingPortal ? "Abrindo..." : "Gerenciar Assinatura"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/settings")}
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <CreditCard className="w-4 h-4" />
            Configurações
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};
